import { Fragment, memo, type ReactNode } from "react";
import {
  AssignmentLateOutlined,
  FactCheckOutlined,
  CheckCircleOutline,
  HighlightOffOutlined,
  HourglassEmptyOutlined,
  PeopleOutline,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  isDocDependentFilter,
  PATIENT_OP_FILTER_GROUPS,
  PATIENT_OP_FILTER_OPTIONS,
  type PatientOpFilter,
} from "../helpers/patientListFilters";

interface EcommercePatientQuickStatsProps {
  totalCount: number;
  docsPendingCount: number;
  docsRejectedCount: number;
  docsToApproveCount: number;
  awaitingReleaseCount: number;
  docsReleasedCount: number;
  purchaseCompletedCount: number;
  activeFilter: PatientOpFilter;
  statsReady?: boolean;
  onFilterChange: (filter: PatientOpFilter) => void;
}

const ICONS: Record<PatientOpFilter, ReactNode> = {
  docs_pending: <AssignmentLateOutlined sx={{ fontSize: 20 }} />,
  docs_rejected: <HighlightOffOutlined sx={{ fontSize: 20 }} />,
  docs_to_approve: <FactCheckOutlined sx={{ fontSize: 20 }} />,
  awaiting_release: <HourglassEmptyOutlined sx={{ fontSize: 20 }} />,
  docs_released: <CheckCircleOutline sx={{ fontSize: 20 }} />,
  purchase_completed: <ShoppingBagOutlined sx={{ fontSize: 20 }} />,
  all: <PeopleOutline sx={{ fontSize: 20 }} />,
};

export default memo(function EcommercePatientQuickStats({
  totalCount,
  docsPendingCount,
  docsRejectedCount,
  docsToApproveCount,
  awaitingReleaseCount,
  docsReleasedCount,
  purchaseCompletedCount,
  activeFilter,
  statsReady = true,
  onFilterChange,
}: EcommercePatientQuickStatsProps) {
  const theme = useTheme();

  const values: Record<PatientOpFilter, number> = {
    docs_pending: docsPendingCount,
    docs_rejected: docsRejectedCount,
    docs_to_approve: docsToApproveCount,
    awaiting_release: awaitingReleaseCount,
    docs_released: docsReleasedCount,
    purchase_completed: purchaseCompletedCount,
    all: totalCount,
  };

  const colors: Record<PatientOpFilter, string> = {
    docs_pending: theme.palette.warning.main,
    docs_rejected: theme.palette.error.main,
    docs_to_approve: theme.palette.secondary.main,
    awaiting_release: theme.palette.info.main,
    docs_released: theme.palette.success.main,
    purchase_completed: theme.palette.success.dark,
    all: theme.palette.primary.main,
  };

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      alignItems="stretch"
    >
      {PATIENT_OP_FILTER_GROUPS.map((group, groupIndex) => (
        <Fragment key={group.id}>
          {groupIndex > 0 ? (
            <Box
              aria-hidden
              sx={{
                display: { xs: "none", md: "block" },
                width: 3,
                alignSelf: "stretch",
                my: 0.75,
                borderRadius: 1,
                bgcolor: "text.disabled",
                flexShrink: 0,
              }}
            />
          ) : null}
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              p: 1,
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: 0,
              bgcolor: "action.hover",
              borderColor: "divider",
            }}
          >
          <Typography
            variant="caption"
            color="text.primary"
            fontWeight={700}
            sx={{
              letterSpacing: 0.4,
              textTransform: "uppercase",
              fontSize: "0.68rem",
              px: 0.25,
            }}
          >
            {group.title}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ flex: 1 }}
          >
            {group.filters.map((filterId) => {
              const option = PATIENT_OP_FILTER_OPTIONS.find(
                (item) => item.id === filterId,
              );
              const selected = activeFilter === filterId;
              const disabled = isDocDependentFilter(filterId) && !statsReady;
              const color = colors[filterId];
              const tooltip = disabled
                ? "Carregando status de documentos e liberações…"
                : option?.description ?? option?.label ?? filterId;

              return (
                <Tooltip key={filterId} title={tooltip} placement="top">
                  <span
                    style={{
                      display: "flex",
                      flex: "1 1 148px",
                      minWidth: 0,
                    }}
                  >
                    <Card
                      component="button"
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        onFilterChange(filterId);
                      }}
                      sx={{
                        p: 1,
                        width: "100%",
                        minHeight: 56,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        textAlign: "left",
                        cursor: disabled ? "not-allowed" : "pointer",
                        border: "1px solid",
                        borderColor: selected ? color : `${color}30`,
                        bgcolor: selected ? `${color}08` : "background.paper",
                        boxShadow: selected ? 1 : 0,
                        opacity: disabled ? 0.72 : 1,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": disabled
                          ? {}
                          : {
                              borderColor: color,
                              boxShadow: 1,
                            },
                        "&.Mui-disabled": {
                          opacity: 0.72,
                          color: "inherit",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          bgcolor: `${color}14`,
                          color,
                        }}
                      >
                        {ICONS[filterId]}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        {disabled ? (
                          <Skeleton variant="text" width={28} height={24} />
                        ) : (
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            lineHeight={1.1}
                            color={color}
                            sx={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {values[filterId]}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.25,
                          }}
                        >
                          {option?.label ?? filterId}
                        </Typography>
                      </Box>
                    </Card>
                  </span>
                </Tooltip>
              );
            })}
          </Stack>
        </Paper>
        </Fragment>
      ))}
    </Stack>
  );
});
